import { ObservableValue, Trace } from '@sensenet/client-utils';
/**
 * Event hub for sensenet Repository Events
 */
export class EventHub {
  /**
   * Disposes the event hub and all of its ObservableValues
   */
  dispose() {
    for (const key in this) {
      if (Object.prototype.hasOwnProperty.call(this, key) && typeof this[key].dispose === 'function') {
        // do not dispose repository
        key !== 'repository' && this[key].dispose();
      }
    }
  }
  /**
   * Triggered after a succesful Content creation
   */

  /**
   * Triggered after Content creation has been failed
   */

  /**
   * Triggered after modifying a Content
   */

  /**
   * Triggered when failed to modify a Content
   */

  /**
   * Triggered when a Content is loaded from the Repository
   */

  /**
   * Triggered when a Content load has been failed
   */

  /**
   * Triggered when a collection of content is loaded from the Repository
   */

  /**
   * Triggered when failed to load a collection of content from the Repository
   */

  /**
   * Triggered after deleting a Content
   */

  /**
   * Triggered after deleting a content has been failed
   */

  /**
   * Triggered after a custom OData Action has been executed
   */

  /**
   * Triggered after a custom OData Action has been failed
   */

  /**
   * Triggered after moving a content to another location
   */

  /**
   * Triggered after moving a content has been failed
   */

  /**
   * Triggered after copying a content to another location
   */

  /**
   * Triggered after copying a content has been failed
   */

  constructor(repository) {
    this.repository = repository;
    this.onContentCreated = new ObservableValue();
    this.onContentCreateFailed = new ObservableValue();
    this.onContentModified = new ObservableValue();
    this.onContentModificationFailed = new ObservableValue();
    this.onContentLoaded = new ObservableValue();
    this.onContentLoadFailed = new ObservableValue();
    this.onContentCollectionLoaded = new ObservableValue();
    this.onContentCollectionLoadFailed = new ObservableValue();
    this.onContentDeleted = new ObservableValue();
    this.onContentDeleteFailed = new ObservableValue();
    this.onCustomActionExecuted = new ObservableValue();
    this.onCustomActionFailed = new ObservableValue();
    this.onContentMoved = new ObservableValue();
    this.onContentMoveFailed = new ObservableValue();
    this.onContentCopied = new ObservableValue();
    this.onContentCopyFailed = new ObservableValue();
    this.onUploadFinished = new ObservableValue();
    this.onUploadFailed = new ObservableValue();
    this.traceObservers = [];
    this.initializeMappings();
  }
  initializeMappings() {
    this.traceObservers.push(Trace.method({
      object: this.repository,
      method: this.repository.post,
      methodName: 'post',
      isAsync: true,
      // Post finished to Content Create
      onFinished: async pushPromise => {
        const response = await pushPromise.returned;
        const content = response.d;
        this.onContentCreated.setValue({
          content: content
        });
      },
      // Post errored to content create failed
      onError: err => {
        this.onContentCreateFailed.setValue({
          content: err.methodArguments[0].content,
          error: err.error
        });
      }
    }), Trace.method({
      object: this.repository,
      method: this.repository.patch,
      methodName: 'patch',
      isAsync: true,
      // Patch finished to ContentModified
      onFinished: async patchPromise => {
        const response = await patchPromise.returned;
        this.onContentModified.setValue({
          changes: patchPromise.methodArguments[0].content,
          content: response.d,
          forceRefresh: patchPromise.methodArguments[0].forceRefresh
        });
      },
      // Patch error to ContentModificationFailed
      onError: error => {
        this.onContentModificationFailed.setValue({
          content: error.methodArguments[0].content,
          error: error.error
        });
      }
    }), Trace.method({
      object: this.repository,
      method: this.repository.put,
      methodName: 'put',
      isAsync: true,
      // Put finished to ContentModified
      onFinished: async putPromise => {
        const response = await putPromise.returned;
        this.onContentModified.setValue({
          changes: putPromise.methodArguments[0].content,
          content: response.d,
          forceRefresh: putPromise.methodArguments[0].forceRefresh
        });
      },
      // Patch error to ContentModificationFailed
      onError: error => {
        this.onContentModificationFailed.setValue({
          content: error.methodArguments[0].content,
          error: error.error
        });
      }
    }), Trace.method({
      object: this.repository,
      method: this.repository.delete,
      methodName: 'delete',
      isAsync: true,
      // handle DeleteBatch finished based on the response value
      onFinished: async deletePromise => {
        const response = await deletePromise.returned;
        if (response.d.results.length) {
          this.onContentDeleted.setValue({
            permanently: deletePromise.methodArguments[0].permanent || false,
            contentData: response.d.results
          });
        }
        if (response.d.errors.length) {
          for (const failed of response.d.errors) {
            this.onContentDeleteFailed.setValue({
              permanently: deletePromise.methodArguments[0].permanent || false,
              content: failed.content,
              error: failed.error
            });
          }
        }
      },
      // Handle DeleteBatch errors
      onError: error => {
        let contentArgs = error.methodArguments[0].idOrPath;
        if (!(contentArgs instanceof Array)) {
          contentArgs = [contentArgs];
        }
        const contents = contentArgs.map(v => {
          return isNaN(v) ? {
            Path: v
          } : {
            Id: parseInt(v, 10)
          };
        });
        for (const c of contents) {
          this.onContentDeleteFailed.setValue({
            content: c,
            permanently: error.methodArguments[0].permanent || false,
            error: error.error
          });
        }
      }
    }), Trace.method({
      object: this.repository,
      method: this.repository.copy,
      methodName: 'copy',
      isAsync: true,
      // handle CopyBatch finished based on the response value
      onFinished: async copyPromise => {
        const response = await copyPromise.returned;
        if (response.d.results.length) {
          this.onContentCopied.setValue({
            content: response.d.results
          });
        }
        if (response.d.errors.length) {
          for (const failed of response.d.errors) {
            this.onContentCopyFailed.setValue({
              content: failed.content,
              error: failed.error
            });
          }
        }
      },
      // Handle CopyBatch errors
      onError: error => {
        let contentArgs = error.methodArguments[0].idOrPath;
        if (!(contentArgs instanceof Array)) {
          contentArgs = [contentArgs];
        }
        const contents = contentArgs.map(v => {
          return isNaN(v) ? {
            Path: v
          } : {
            Id: parseInt(v, 10)
          };
        });
        for (const c of contents) {
          this.onContentCopyFailed.setValue({
            content: c,
            error: error.error
          });
        }
      }
    }), Trace.method({
      object: this.repository,
      method: this.repository.move,
      methodName: 'move',
      isAsync: true,
      // handle MoveBatch finished based on the response value
      onFinished: async movePromise => {
        const response = await movePromise.returned;
        if (response.d.results.length) {
          this.onContentMoved.setValue({
            content: response.d.results
          });
        }
        if (response.d.errors.length) {
          for (const failed of response.d.errors) {
            this.onContentMoveFailed.setValue({
              content: failed.content,
              error: failed.error
            });
          }
        }
      },
      // Handle MoveBatch errors
      onError: error => {
        let contentArgs = error.methodArguments[0].idOrPath;
        if (!(contentArgs instanceof Array)) {
          contentArgs = [contentArgs];
        }
        const contents = contentArgs.map(v => {
          return isNaN(v) ? {
            Path: v
          } : {
            Id: parseInt(v, 10)
          };
        });
        for (const c of contents) {
          this.onContentMoveFailed.setValue({
            content: c,
            error
          });
        }
      }
    }), Trace.method({
      object: this.repository,
      method: this.repository.executeAction,
      methodName: 'executeAction',
      isAsync: true,
      onFinished: async actionPromise => {
        const result = await actionPromise.returned;
        this.onCustomActionExecuted.setValue({
          actionOptions: actionPromise.methodArguments[0],
          oDataParams: actionPromise.methodArguments[0].oDataOptions,
          result
        });
      },
      onError: async error => {
        this.onCustomActionFailed.setValue({
          actionOptions: error.methodArguments[0],
          error: error.error,
          oDataParams: error.methodArguments[0].oDataOptions
        });
      }
    }), Trace.method({
      object: this.repository,
      method: this.repository.load,
      methodName: 'load',
      isAsync: true,
      onFinished: async loadPromise => {
        const value = await loadPromise.returned;
        this.onContentLoaded.setValue({
          content: value.d
        });
      },
      onError: async error => {
        this.onContentLoadFailed.setValue({
          payload: error.methodArguments[0],
          error: error.error
        });
      }
    }), Trace.method({
      object: this.repository,
      method: this.repository.loadCollection,
      methodName: 'loadCollection',
      isAsync: true,
      onFinished: async loadPromise => {
        const value = await loadPromise.returned;
        this.onContentCollectionLoaded.setValue(value);
      },
      onError: async error => {
        this.onContentCollectionLoadFailed.setValue({
          payload: error.methodArguments[0],
          error: error.error
        });
      }
    }), Trace.method({
      isAsync: true,
      object: this.repository.upload,
      methodName: 'uploadChunked',
      method: this.repository.upload.uploadChunked,
      onFinished: async promise => {
        const returnValue = await promise.returned;
        this.onUploadFinished.setValue(returnValue);
      },
      onError: e => {
        this.onUploadFailed.setValue({
          error: e.error,
          options: e.methodArguments[0]
        });
      }
    }), Trace.method({
      isAsync: true,
      object: this.repository.upload,
      methodName: 'uploadNonChunked',
      method: this.repository.upload.uploadNonChunked,
      onFinished: async promise => {
        const returnValue = await promise.returned;
        this.onUploadFinished.setValue(returnValue);
      },
      onError: e => {
        this.onUploadFailed.setValue({
          error: e.error,
          options: e.methodArguments[0]
        });
      }
    }));
  }
}
//# sourceMappingURL=EventHub.js.map